import { useEffect, useState, useContext, FC } from 'react';
import Button, { ButtonType } from '../../general-components/Button';
import styles from './RoomTable.module.css';
import RIGHTS_KEYS from '../../../constants/rights-keys';
import AuthContext from '../../../store/auth-context';
import { getAllMRIMachinesByDepartment } from '../../../services/apiService';
import { labelSarRows } from '../../../models/MRIMachineModels';
import ConfirmActionButton from '../../../components/general/confirmation/ConfirmActionButton';
import FileModal from '../../video-overview/FileModal';
import FileLink from '../../files/FileLink';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface RoomTableProps {
    machine: any;
    setMachineIdToEdit: any;
    onDelete: any;
}
const RoomTable: FC<RoomTableProps> = ({
    machine,
    setMachineIdToEdit,
    onDelete,
}) => {
    const context = useContext(AuthContext);
    const [shownFile, setShownFile] = useState<any>(null);
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="pt-4 px-2 rounded-xl">
            {shownFile && (
                <FileModal
                    file={{ ...shownFile, title: shownFile.filename }}
                    close={() => setShownFile(null)}
                />
            )}
            <table className={styles.table}>
                <caption onClick={() => setIsOpen(!isOpen)}>
                    <div
                        style={{
                            marginLeft: '16px',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <FontAwesomeIcon
                            icon={isOpen ? faChevronDown : faChevronUp}
                        />
                        <div className={styles.tableHeadingWrapper}>
                            <div className={styles.tableHeading}>
                                <h4>{machine?.Name}</h4>
                                {context.orRights([
                                    RIGHTS_KEYS.manageMRIOverview,
                                ]) && (
                                    <div
                                        className={
                                            styles.tableHeadingButtonsWrapper
                                        }
                                    >
                                        <Button
                                            small
                                            className={styles.editBtn}
                                            type={ButtonType.tietary}
                                            onClick={() => {
                                                setMachineIdToEdit(machine._id);
                                            }}
                                        >
                                            Edit
                                        </Button>
                                        <ConfirmActionButton
                                            buttonText="Delete"
                                            buttonType={ButtonType.cancel}
                                            modalInfo={{
                                                title: 'Delete MRI Machine',
                                                message: 'Deleted',
                                                submitButtonText: 'Delete',
                                                submitButtonDanger: true,
                                            }}
                                            onConfirm={() => {
                                                onDelete(machine._id);
                                            }}
                                        >
                                            This Cannot be undone
                                        </ConfirmActionButton>
                                    </div>
                                )}
                            </div>
                            <h4>{machine.Model}</h4>
                        </div>
                    </div>
                </caption>
                {isOpen && (
                    <tbody>
                        <tr>
                            <td>Static magnetic field</td>
                            <td>{machine.FieldStrength} Tesla</td>
                        </tr>
                        {machine.SpatialGradient.map(
                            (gradient: any, index: number) => (
                                <tr>
                                    <td>
                                        Spatial gradient - Diameter:{' '}
                                        {gradient.diameter} cm
                                    </td>
                                    <td>
                                        {gradient.value} T/m (
                                        {Math.floor(gradient.value * 100)}{' '}
                                        Gauss/cm)
                                    </td>
                                </tr>
                            )
                        )}

                        <tr>
                            <td>Maximun slew rate {'(per axis)'}</td>
                            <td>{machine.SlewRatePerAxis} T/m/s</td>
                        </tr>
                        <tr>
                            <td>Maximum effective slew rate</td>
                            <td>{machine.MaxSlewRate} T/m/s</td>
                        </tr>
                        <tr>
                            <td>Maximum SAR - RF energy deposition</td>{' '}
                            <td>
                                {labelSarRows(machine.MaxSAR).map(
                                    (sar: any, i: number) => (
                                        <li>
                                            {sar.label && `${sar.label} - `}
                                            WholeBody {sar.wholeBody} W/kg -
                                            Head {sar.head} W/kg
                                        </li>
                                    )
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Maximum B1+rms</td>
                            <td>
                                {machine.MaxB1rms}{' '}
                                {!isNaN(machine.MaxB1rms) ? '\u00B5T' : ''}
                            </td>
                        </tr>
                        <tr>
                            <td>T/R Coils</td>
                            <td>{machine.TRCoils}</td>
                        </tr>
                        {machine.attachedFiles?.length > 0 && (
                            <tr>
                                <td>Attached Files</td>
                                <td
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '10px',
                                    }}
                                >
                                    {machine.attachedFiles.map((file: any) => (
                                        <FileLink
                                            key={file._id}
                                            file={file}
                                            onClick={setShownFile}
                                        />
                                    ))}
                                </td>
                            </tr>
                        )}
                    </tbody>
                )}
            </table>
        </div>
    );
};

export default RoomTable;
